import React, { useEffect, useState } from 'react';
import { ReviewCardsOrderProperty, IReviewCardsOrderProperty } from '../../interfaces/review_cards';
import { DataRows } from '../../interfaces/common';
import * as ReviewCardsApi from '../../api/review_cards';
import DataTable, { DataTableSortOrder } from '../../components/common/data_table';
import _ from 'lodash';
import { LoadingDots } from '../../components/common/loading_dots';
import { showToast, ToastType } from '../../utils/toast';
import Modal from 'react-bootstrap/Modal';
interface IProps {
    data: any;
}

export const OrderHistoryDetails = ({data}: IProps) => {

    const [busyLoadingSubOrders, setBusyLoadingSubOrders] = useState<boolean>();
    const [subOrders, setSubOrders] = useState<ReviewCardsOrderProperty[]>([]);
    const [busyDeletingOrder, setBusyDeletingOrder] = useState<boolean>(false);
    const [propertyOrderRowToDelete, setPropertyOrderRowToDelete] = useState<IReviewCardsOrderProperty>();
    const [showPropertyOrderDeletionModal, setShowPropertyOrderDeletionModal] = useState<boolean>(false);

    const {PostalCardsOrderID} = data;

    const getSubOrders = async (orderID: number): Promise<DataRows<ReviewCardsOrderProperty>> => {
        return await ReviewCardsApi.getOrder(orderID, {
            SortField: 'Name',
            SortOrder: DataTableSortOrder.Ascending
        });
    };

    useEffect(() => {
        if (PostalCardsOrderID) {
            setBusyLoadingSubOrders(true);
            (async () => {
                try {
                    const resp = await getSubOrders(PostalCardsOrderID);
                    const subOrderData = _.get(resp, 'data', []);
                    setSubOrders(subOrderData);
                } catch (e) {
                    console.error(e);
                } finally {
                    setBusyLoadingSubOrders(false);
                }
            })();
        } else {
            setSubOrders(null);
        }
    }, [PostalCardsOrderID]);

    const PropertyNameElement = function statelessFunctionComponentClass(props: any) {
        const formatAddress = (property: IReviewCardsOrderProperty) => {
            return [
                property.Address1,
                property.Address2,
                property.Address3,
                property.City,
                property.RegionCode,
                property.PostCode
            ].reduce((total: string, value: string) => {
                return value && value.length > 0 ? `${total}, ${value}` : total;
            });
        };

        return (
            <div className="flex flex-wrap gap-2 break-words py-1">
                <strong className="block leading-tight lg:text-lg">
                    {props.row.PropertyName}
                </strong>
                <span className="inline-block text-xs lg:text-sm leading-tight text-brand_grey-medium">
                    {formatAddress(props.row)}
                </span>
            </div>
        );
    };

    const subOrdersColumns = [
        {
            name: 'Property',
            selector: (row: IReviewCardsOrderProperty) => row.PropertyName,
            sortable: true,
            sortField: 'PropertyName',
            width: '*',
            cell: (row: IReviewCardsOrderProperty) => <PropertyNameElement row={row}/>
        }, {
            name: 'Recipient First Name',
            selector: (row: IReviewCardsOrderProperty) => row.RecipientFirstName,
            sortable: true,
            sortField: 'RecipientFirstName',
            width: '*'
        }, {
            name: 'Recipient Last Name',
            selector: (row: IReviewCardsOrderProperty) => row.RecipientLastName,
            sortable: true,
            sortField: 'RecipientLastName',
            width: '*'
        }, {
            name: 'Review Postal Cards',
            selector: (row: IReviewCardsOrderProperty) => row.CardsQuantity,
            sortable: true,
            sortField: 'CardsQuantity',
            width: '*',
            cell: (row: IReviewCardsOrderProperty) => {
                return row.CardsQuantity ?
                    <i className="fa fa-solid fa-check">{row.CardsQuantity}</i>
                    :
                    null;
            }
        }, {
            name: 'Review Postal Stands',
            selector: (row: IReviewCardsOrderProperty) => row.StandsQuantity,
            sortable: true,
            sortField: 'StandsQuantity',
            width: '*',
            cell: (row: IReviewCardsOrderProperty) => {
                return row.StandsQuantity ?
                    <i className="fa fa-solid fa-check">{row.StandsQuantity}</i>
                    :
                    null;
            }
        }, 
        // TODO: this will be uncommented when business is ready to produce/print QR Code cards and stands. 
        // {
        //     name: 'QR Code Business Cards',
        //     selector: (row: IReviewCardsOrderProperty) => row.QRCodeBusinessCardsQuantity,
        //     sortable: true,
        //     sortField: 'QRCodeBusinessCardsQuantity',
        //     width: '*',
        //     cell: (row: IReviewCardsOrderProperty) => {
        //         return row.CardsQuantity ?
        //             <i className="fa fa-solid fa-check">{row.QRCodeBusinessCardsQuantity}</i>
        //             :
        //             null;
        //     }
        // }, {
        //     name: 'QR Code Stands',
        //     selector: (row: IReviewCardsOrderProperty) => row.QRCodeStandsQuantity,
        //     sortable: true,
        //     sortField: 'QRCodeStandsQuantity',
        //     width: '*',
        //     cell: (row: IReviewCardsOrderProperty) => {
        //         return row.StandsQuantity ?
        //             <i className="fa fa-solid fa-check">{row.QRCodeStandsQuantity}</i>
        //             :
        //             null;
        //     }
        // }, 
        {
            width: '*',
            cell: (row: IReviewCardsOrderProperty) => {
                if (!row.ShippedAt) {
                    return <button
                        className="btn btn-error ms-auto"
                        type="button"
                        onClick={() => {
                            setPropertyOrderRowToDelete(row);
                            setShowPropertyOrderDeletionModal(true);
                        }}>
                        Delete
                    </button>;
                }
            }
        }
    ];

    const propertyOrderDeletionModal = (): JSX.Element => {
        return (
            <Modal show={true} centered size="sm">
                <Modal.Header>
                    <Modal.Title>
                        Confirm
                    </Modal.Title>
                </Modal.Header>
                <form>
                    <Modal.Body>
                        <div className="text-center">
                            {'Sure that you want to delete order for property'}?
                            <div className="mt-3">
                                <button
                                    className="btn btn-sm btn-primary"
                                    type="button"
                                    disabled={busyDeletingOrder}
                                    onClick={() => setShowPropertyOrderDeletionModal(false)}>
                                    Cancel
                                </button>
                                &nbsp;&nbsp;
                                <button
                                    className="btn btn-sm btn-danger ml-10"
                                    type="button"
                                    disabled={busyDeletingOrder}
                                    onClick={() => deletePropertyOrder(propertyOrderRowToDelete.PostalCardsOrderPropertyID)}>
                                    Delete
                                </button>
                            </div>
                        </div>
                    </Modal.Body>
                </form>
            </Modal>
        );
    };

    const deletePropertyOrder = (orderID: number) => {
        (async () => {
            setBusyDeletingOrder(true);
            try {
                await ReviewCardsApi.deleteOrderRow(orderID);
                showToast('Property Order Deleted', ToastType.Success);
                setSubOrders(subOrders.filter((subOrder: any) => subOrder.PostalCardsOrderPropertyID !== orderID));
            } catch (e) {
                showToast('Property Order Deletion Failed', ToastType.Error);
                console.error(e);
            } finally {
                setBusyDeletingOrder(false);
                setShowPropertyOrderDeletionModal(false);
                setPropertyOrderRowToDelete(null);
            }
        })();
    };
    
    return (
        <>
            <div className="py-4">
                {busyLoadingSubOrders ? <LoadingDots show={true}/> : null}
                <div className="px-4 pb-4">
                    <strong>Order Number:</strong> {PostalCardsOrderID}
                </div>
                <DataTable
                    data={subOrders}
                    columns={subOrdersColumns}
                    pagination={false}
                    initialRowsPerPage={10}
                    defaultSortFieldId={1}
                    fixedHeader={true}
                    noDataComponent={<span></span>}
                />
            </div>
            {
                showPropertyOrderDeletionModal ? propertyOrderDeletionModal() : null
            }
        </>
    );
};
